import { useCallback, useEffect, useRef, useState } from 'react'
import { OrgSegmentModel } from '../models/segments/org-segment.model'
import { SegmentModel } from '../models/segments/segment.model'
import { useIfMountedCallback, useService } from '@pushly/aqe/lib/hooks'
import { DomainService } from '../services'
import { useCachedValue } from './use-cached-value'
import axios, { CancelTokenSource } from 'axios'

export type SegmentRemoteReachApi = {
    readonly loading: boolean
    readonly reach: number | null
    reload(): Promise<void>
}

type SegmentRemoteReachOptions = {
    cache?:
        | boolean
        | {
              ttl: number
          }
}

/**
 * useSegmentRemoteReach
 *
 * Fetches the live (remote) audience size for a segment and returns it with loading state and a manual reload API.
 * Designed for list/table usage where the hook is instantiated per row and should avoid redundant refetches via
 * a sessionStorage-backed cache. Uses request cancellation to prevent race conditions and avoids setting state
 * after unmount.
 *
 * Behavior:
 * - Only fetches for domain-level segments (SegmentModel) that are in READY state.
 * - Org-level segments (OrgSegmentModel) never fetch remotely; the hook will resolve to null for reach.
 * - Optionally caches the numeric reach in sessionStorage with an optional TTL, preventing unnecessary re-fetches
 *   across simple navigations.
 * - Cancels in-flight requests when the component unmounts or when a new fetch supersedes the previous one.
 *
 * Caching:
 * - When enabled, the hook reads an existing cached value and starts in non-loading state if present.
 * - When a new remote value is obtained, it writes to the cache (unless the request was aborted).
 * - Cache is keyed by segment type and ID: swc_sr_s<ID> for SegmentModel, swc_sr_os<ID> for OrgSegmentModel.
 *
 * Concurrency and safety:
 * - Uses an AbortController to cancel requests on unmount or reload.
 * - Uses an "is mounted" guard to prevent state updates after unmount.
 *
 * Example:
 * ```tsx
 * const { reach, loading, reload } = useSegmentRemoteReach(segment, { cache: { ttl: 60_000 } })
 * ```
 *
 * @param segment The segment to query. Domain-level SegmentModel may fetch live reach; OrgSegmentModel will not fetch.
 * @param options Optional configuration.
 * @param options.cache Enable caching. When true, enables sessionStorage caching with no TTL.
 *                      When an object is provided, you can specify a TTL.
 *                      When omitted or false, caching is disabled.
 *
 * @returns API with:
 * - loading: boolean — true while a remote fetch is in progress (or no cache available yet).
 * - reach: number | null — the remote audience size if available; null if not ready or not applicable.
 * - reload: () => Promise<void> — triggers a fresh remote fetch (respects READY status and segment type rules).
 *
 * Notes:
 * - If the segment is not READY, the hook will set reach to null and loading to false without performing a request.
 * - For OrgSegmentModel, reach will remain null (use local/aggregate values externally if needed).
 * - For list views, consider batching calls at a higher level if many rows request reach simultaneously.
 */
export const useSegmentRemoteReach = (
    segment: OrgSegmentModel | SegmentModel,
    { cache: cacheOptions = false }: SegmentRemoteReachOptions = {},
): SegmentRemoteReachApi => {
    const domainSvc = useService(DomainService)
    const runIfMounted = useIfMountedCallback()

    const segmentId = segment.getId()
    const isDomainSegment = segment instanceof SegmentModel
    const computedStatus = segment.getComputedStatus()
    const domainId = isDomainSegment ? segment.getDomainId() : null
    const channels = segment.getChannels()

    const usingCachedValue = !!cacheOptions
    const cacheKey = `swc_sr_${isDomainSegment ? 's' : 'os'}${segmentId}`
    const cachedReach = useCachedValue(cacheKey, {
        ttl: !cacheOptions || cacheOptions === true ? undefined : cacheOptions.ttl,
        parser: (v) => (v ? Number(v) : null),
    })

    // Use state for loading to trigger re-renders
    const [loading, setLoading] = useState<boolean>(() => {
        // If we have cached data, start as not loading
        return !(usingCachedValue && cachedReach.isNotEmpty())
    })

    const [reach, setReach] = useState<number | null>(() => {
        return usingCachedValue ? cachedReach.get() : null
    })

    // Track in-flight request for cancellation
    const axiosCancelTokenSourceRef = useRef<CancelTokenSource | null>(null)
    const loadingRef = useRef<boolean>(false)

    const loadRemoteReach = useCallback(async () => {
        // Prevent duplicate requests
        if (loadingRef.current) {
            console.warn(`Remote reach already loading for segment ${segmentId}`)
            return
        }

        // Cancel any in-flight request
        if (axiosCancelTokenSourceRef.current) {
            axiosCancelTokenSourceRef.current.cancel()
        }

        // Only domain segments in the ready state can remote fetch
        if (computedStatus !== 'READY' || !isDomainSegment || !domainId) {
            runIfMounted(() => {
                setReach(null)
                setLoading(false)
            })

            return
        }

        loadingRef.current = true
        setLoading(true)

        // Create abort controller for this request
        axiosCancelTokenSourceRef.current = axios.CancelToken.source()

        let res: number | null = null

        try {
            const { data, cancelled } = await domainSvc.estimateSegmentReach(
                domainId!,
                [segmentId],
                [],
                channels,
                'count',
                ['NOTIFICATION'],
                {
                    cancellationToken: axiosCancelTokenSourceRef.current.token,
                },
            )

            res = data ?? null
            if (!cancelled && res !== null && usingCachedValue) {
                cachedReach.set(res)
            }
        } catch (e) {
            // Only log if not an abort error
            if (!axios.isCancel(e)) {
                console.warn('Failed to load remote reach', e)
            }
            res = null
        } finally {
            loadingRef.current = false
            axiosCancelTokenSourceRef.current = null

            runIfMounted(() => {
                setLoading(false)
                setReach(res)
            })
        }
    }, [segmentId, isDomainSegment, computedStatus, domainId, channels, domainSvc, runIfMounted, cachedReach.set])

    useEffect(() => {
        // Only fetch if we don't have cached data
        if (!usingCachedValue || cachedReach.isEmpty()) {
            loadRemoteReach()
        }

        // Cleanup: cancel any in-flight requests on unmount
        return () => {
            if (axiosCancelTokenSourceRef.current) {
                axiosCancelTokenSourceRef.current.cancel()
                axiosCancelTokenSourceRef.current = null
            }
        }
    }, []) // no dependencies since this effect should ONLY EVER RUN ONCE

    return {
        get loading() {
            return loading
        },
        get reach() {
            return reach
        },
        reload: () => loadRemoteReach(),
    } satisfies SegmentRemoteReachApi
}
